/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { convertToUTCDate } from '@/utils/convertToUTCDate';
import { Component } from 'types/api-data';

import { multiSelectDataTransform } from '@/lib/helper';
import { useAppDetails } from '@/hooks/useAppDetails';
import {
  getPresetLabelToNameMap,
  getPresetRange,
  usePresets,
} from '@/components/display-page/prompts/date-range-picker/calendar-helper/presets';

export type ViewByOption = 'all' | 'performance' | 'reliability';
export type ActiveTabOption = 'time' | 'search' | 'most_recent';
export type Fact = { key: string; label: string };

interface Props {
  workflowName: string;
  facts: Fact[] | null;
  breakdowns: Record<string, string> | null;
}

export interface TimeRange {
  startTime: string | null;
  endTime: string | null;
  startDurationMs?: number | null;
  endDurationMs?: number | null;
}

export interface TracesFilters {
  factName: string | null;
  breakdownFilter: string | null;
  activeTab: ActiveTabOption;
  timeRange: TimeRange;
  viewBy: ViewByOption;
  workflowNames: string | null;
  evaluation?: string | null;
  mostRecent?: number | null;
  filterFact?: string | null;
  filterFactIDs?: string | null;
}

const FACT_VIEW_BREAKDOWN_MAP: Record<
  string,
  Partial<Record<ViewByOption, string>>
> = {
  traces: {
    all: 'traces',
    performance: 'traces.genai.completed',
    reliability: 'traces.genai.error',
  },
  inferences: {
    all: 'inferences',
    performance: 'inferences.completed',
    reliability: 'inferences.errors',
  },
  http: {
    all: 'http',
    performance: 'http.completed',
    reliability: 'http.errors',
  },
  agent_requests: {
    all: 'agent_requests',
    performance: 'agent_requests.completed',
    reliability: 'agent_requests.errors',
  },
  agent_operations: {
    all: 'agent_operations',
    performance: 'agent_operations.completed',
    reliability: 'agent_operations.errors',
  },
  tool_operations: {
    all: 'tool_operations',
    performance: 'tool_operations.completed',
    reliability: 'tool_operations.errors',
  },
  agent_sessions: {
    all: 'agent_sessions',
    performance: 'agent_sessions.completed',
    reliability: 'agent_sessions.errors',
  },
  git_commits: {
    all: 'git_commits',
    performance: 'git_commits',
    reliability: 'git_commits',
  },
  test_runs: {
    all: 'test_runs',
    performance: 'test_runs',
    reliability: 'test_runs',
  },
  tests: {
    all: 'tests',
    performance: 'tests',
    reliability: 'tests',
  },
  conversations: {
    all: 'conversations',
    performance: 'conversations',
    reliability: 'conversations',
  },
  mcp_invocations: {
    all: 'mcp_invocations',
    performance: 'mcp_invocations',
    reliability: 'mcp_invocations',
  },
  // other facts can be added explicitly later if needed
};

// Add fact names here that only support the 'all' view (and do not have performance/reliability views, e.g., git_commits or tenants)
export const FACTS_WITHOUT_TABS: string[] = [
  'tenants',
  'git_commits',
  'tests',
  'test_runs',
  'mcp_invocations',
];

export const FACT_VIEW_SUPPORT_MAP: Record<string, ViewByOption[]> = new Proxy<
  Record<string, ViewByOption[]>
>(
  {},
  {
    get: (target, prop: string) => {
      if (typeof prop !== 'string') return undefined;
      if (FACTS_WITHOUT_TABS.includes(prop)) {
        return ['all'];
      }
      return ['all', 'performance', 'reliability'];
    },
  }
);

const DEFAULT_VIEW_BY: ViewByOption = 'all';
const DEFAULT_ACTIVE_TAB: ActiveTabOption = 'time';

// purpose: manage traces filters and query params which needs to go in api calls, to be in sync with URL. no api calls neither any state related to data fetching should be handled here.
export function useTracesFilters({ workflowName, facts, breakdowns }: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const isComponentsPage = pathname.includes('/components');

  // app default preset logic
  const presets = usePresets();
  //used in workflow filter dropdown in traces page.
  const {
    loading: workflowLoading,
    data,
    defaultFact,
  } = useAppDetails() || {
    loading: false,
    data: null,
    defaultFact: null,
  };
  const presetMap = getPresetLabelToNameMap(presets);
  const defaultPresetName = presetMap[data?.default_time_range] || 'last7';
  const range = getPresetRange(defaultPresetName, presets);
  const lastUrlRef = useRef<string>('');

  //we start with null filters, then we hydrate either from URL or defaults.
  const [filters, setFilters] = useState<TracesFilters | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [isOriginFromMeasuresBadge, setIsOriginFromMeasuresBadge] = useState(
    searchParams.get('from_measures_badge') === '1'
  );

  const intentRef = useRef<{
    factName: string | null;
    breakdownFilter: string | null;
    viewBy: ViewByOption;
    workflowNames: string | null;
    activeTab: ActiveTabOption;
    timeRange: TimeRange;
    evaluation?: string | null;
    filterFact?: string | null;
    filterFactIDs?: string | null;
    mostRecent?: number | null;
  } | null>(null);

  const workflows = isComponentsPage
    ? [workflowName]
    : data?.components?.filter((component: Component) =>
        component.type.includes('workflow')
      ) || [];

  const transformedWorkflows = multiSelectDataTransform(workflows) ?? [];

  // initial hydration from URL or defaults
  useEffect(() => {
    if (hydrated) return;

    const hasParams = searchParams.toString().length > 0;
    // ------------------------------------------
    // STEP 1 — Parse URL / Defaults ONCE (intent)
    // ------------------------------------------
    if (!intentRef.current) {
      const days = searchParams.get('days');
      // const isOriginFromMeasuresBadge =
      //   searchParams.get('from_measures_badge') === '1';

      // setIsOriginFromMeasuresBadge(isOriginFromMeasuresBadge);

      intentRef.current = {
        factName: hasParams ? searchParams.get('factName') : null,
        breakdownFilter: hasParams ? searchParams.get('breakdownFilter') : null,
        viewBy: (searchParams.get('viewBy') as ViewByOption) || DEFAULT_VIEW_BY,
        workflowNames: hasParams
          ? searchParams.get('workflowNames') || workflowName
          : workflowName,
        activeTab:
          (searchParams.get('activeTab') as ActiveTabOption) ||
          DEFAULT_ACTIVE_TAB,
        evaluation: hasParams
          ? searchParams.get('eval') || searchParams.get('evaluation')
          : null,
        timeRange: {
          startTime:
            days === 'all'
              ? null
              : searchParams.get('start') ||
                convertToUTCDate(range.from)?.toString() ||
                null,
          endTime:
            days === 'all'
              ? null
              : searchParams.get('end') ||
                convertToUTCDate(range.to)?.toString() ||
                null,
          startDurationMs: null,
          endDurationMs: null,
        },
        filterFact: hasParams ? searchParams.get('filterFact') : null,
        filterFactIDs: hasParams ? searchParams.get('filterFactIDs') : null,
        mostRecent:
          hasParams && searchParams.get('mostRecent')
            ? Number(searchParams.get('mostRecent'))
            : null,
      };
    }

    if (!intentRef.current) return;
    const intent = intentRef.current;

    // --------------------
    // STEP 1.5 - early commit for static things: viewBy and activeTab coz until they come, UI doesn't render the sections below toolBar-container.
    // --------------------
    if (!filters?.viewBy && !filters?.activeTab) {
      const defaultFilters: TracesFilters = {
        // Keep factName null on the /components route — setting it (e.g. to
        // 'traces') triggers an extra traces/eval API call. The Evaluate modal
        // is given 'traces' explicitly for workflows instead.
        factName: null,
        breakdownFilter: null,
        viewBy: intent.viewBy,
        activeTab: intent.activeTab,
        workflowNames: intent.workflowNames,
        evaluation: intent.evaluation,
        timeRange: intent.timeRange,
        mostRecent: intent.mostRecent ?? null,
      };
      setFilters(defaultFilters);
    }
    //in case of workflows on /components route, we dont have facts and breakdowns, so we accept what's in the intent (already set in above condition) and return.
    if (isComponentsPage) {
      setHydrated(true);
      return;
    }

    // ------------------------------------------
    // STEP 2 — Wait for FACTS
    // ------------------------------------------
    if (!facts || facts.length === 0) return;

    const requestedFact = intent.factName || defaultFact;
    const resolvedFact =
      requestedFact && facts.find((fact) => fact.key === requestedFact)
        ? requestedFact
        : facts[0].key;
    const requestedFilterFact = intent.filterFact;
    const resolvedFilterFact =
      requestedFilterFact &&
      facts.find((fact) => fact.key === requestedFilterFact)
        ? requestedFilterFact
        : null;
    const filterFactIDs = intent.filterFactIDs || null;
    const supportedViews = getSupportedViewsForFact(resolvedFact);
    const resolvedViewBy = intent.viewBy
      ? supportedViews.includes(intent.viewBy)
        ? intent.viewBy
        : supportedViews[0]
      : supportedViews[0];
    // ------------------------------------------
    // STEP 2.5 — COMMIT FACT EARLY (once)
    // ------------------------------------------
    if (!filters?.factName) {
      setFilters((prev) => ({
        ...(prev ?? {
          factName: null,
          filterFact: resolvedFilterFact,
          breakdownFilter: null,
          viewBy: resolvedViewBy,
          activeTab: intent.activeTab,
          workflowNames:
            searchParams.get('workflowNames') || workflowName || null,
          evaluation: intent.evaluation,
          timeRange: intent.timeRange,
          filterFactIDs: filterFactIDs,
          mostRecent: intent.mostRecent ?? null,
        }),
        factName: resolvedFact,
        filterFact: resolvedFilterFact,
        filterFactIDs: filterFactIDs,
      }));
      return; // wait for breakdowns fetch
    }

    // ------------------------------------------
    // STEP 3 — Wait for BREAKDOWNS
    // ------------------------------------------
    if (
      !breakdowns ||
      typeof breakdowns !== 'object' ||
      Array.isArray(breakdowns)
    ) {
      return;
    }

    const breakdownKeys = Object.keys(breakdowns);
    if (breakdownKeys.length === 0) return;

    const requestedBreakdown = intent.breakdownFilter;
    const resolvedBreakdown =
      requestedBreakdown &&
      (isOriginFromMeasuresBadge || breakdownKeys.includes(requestedBreakdown))
        ? requestedBreakdown
        : getDefaultBreakdownForFact(resolvedFact, intent.viewBy);

    // ------------------------------------------
    // STEP 4 — FINAL COMMIT + HYDRATE
    // ------------------------------------------
    setFilters((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        breakdownFilter: resolvedBreakdown,
        viewBy: resolvedViewBy,
        activeTab: intent.activeTab,
        workflowNames:
          searchParams.get('workflowNames') || workflowName || null,
        evaluation: intent.evaluation,
        timeRange: intent.timeRange,
      };
    });

    setHydrated(true);
    lastUrlRef.current = `${pathname}?${searchParams.toString()}`;
  }, [
    hydrated,
    facts,
    breakdowns,
    defaultFact,
    searchParams,
    pathname,
    workflowName,
    filters?.factName,
  ]);

  // ------------------------------------------
  // UPDATE URL WHEN FILTERS CHANGE
  // ------------------------------------------
  useEffect(() => {
    if (!hydrated || !filters) return;
    const params = new URLSearchParams();

    const inMostRecentMode = filters.mostRecent != null;

    if (filters.activeTab === 'search') {
      const traceId = searchParams.get('trace_id');
      if (traceId) params.set('trace_id', traceId);

      const conversationId = searchParams.get('conversation_id');
      if (conversationId) params.set('conversation_id', conversationId);
    }

    Object.entries(filters).forEach(([key, value]) => {
      if (value === null || value === '') return;

      // On the /components (workflow) route the workflow is already in the path,
      // so workflowNames in the URL is redundant. Writing it here fights the
      // URL<->filters sync and causes the URL to flicker, so skip it.
      if (key === 'workflowNames' && isComponentsPage) return;

      if (key === 'timeRange') {
        if (inMostRecentMode || filters.activeTab === 'search') return;

        if (value.startTime === null && value.endTime === null) {
          params.set('days', 'all');
        } else {
          if (value.startTime) params.set('start', value.startTime);
          if (value.endTime) params.set('end', value.endTime);
        }
        return;
      }

      if (key === 'mostRecent') {
        if (inMostRecentMode) {
          params.set('mostRecent', String(value));
        }
        return;
      }

      if (key === 'evaluation') {
        params.set('eval', String(value));
        return;
      }

      params.set(key, String(value));
    });

    const newUrl = `${pathname}?${params.toString()}`;

    // Only update if URL actually changed
    if (lastUrlRef.current !== newUrl) {
      lastUrlRef.current = newUrl;
      router.replace(newUrl);
    }
  }, [filters, hydrated]);

  // ------------------------------------------
  // UPDATE FILTERS WHEN URL CHANGES while being on same route, e.g navigation from err/perf pills.
  // ------------------------------------------
  useEffect(() => {
    if (!hydrated) return;

    // delay to allow router.replace() to update searchParams from previous useEffect.
    const id = setTimeout(() => {
      const currentUrl = `${pathname}?${searchParams.toString()}`;

      if (lastUrlRef.current === currentUrl) return;
      //if url contains trace_id, it means its a search view, not pills navigation.
      // trace navigation from kahu.
      if (currentUrl.includes('trace_id')) {
        // If URL has trace_id, it's always a search view, infer 'search' if activeTab is missing
        const newActiveTab =
          (searchParams.get('activeTab') as ActiveTabOption) || 'search';
        const newFactName = searchParams.get('factName') || 'traces';

        setFilters((prev) => {
          if (!prev) return prev;

          return {
            ...prev,
            activeTab: newActiveTab,
            factName: newFactName,
            breakdownFilter: null,
            filterFact: null,
            filterFactIDs: null,
          };
        });

        lastUrlRef.current = currentUrl;
        return;
      }

      const isOriginFromMeasuresBadge = searchParams.get('from_measures_badge');
      if (isOriginFromMeasuresBadge !== '1') {
        //its not even pills nav, return;
        return;
      }
      setIsOriginFromMeasuresBadge(true);
      const days = searchParams.get('days');
      const newFilters: TracesFilters = {
        factName: searchParams.get('factName'),
        breakdownFilter: searchParams.get('breakdownFilter'),
        activeTab:
          (searchParams.get('activeTab') as ActiveTabOption) ||
          DEFAULT_ACTIVE_TAB,
        viewBy: (searchParams.get('viewBy') as ViewByOption) || DEFAULT_VIEW_BY,
        workflowNames: workflowName,
        evaluation: searchParams.get('eval') || searchParams.get('evaluation'),
        timeRange: {
          startTime: days === 'all' ? null : searchParams.get('start'),
          endTime: days === 'all' ? null : searchParams.get('end'),
          startDurationMs: null,
          endDurationMs: null,
        },
      };

      setFilters(newFilters);
      lastUrlRef.current = currentUrl;
    }, 100);

    return () => clearTimeout(id);
  }, [searchParams, hydrated, pathname]);

  //used for setting the root breakdown in breakdown_filter when breakdowns change due to factName change.
  useEffect(() => {
    if (!hydrated) return;
    if (filters?.breakdownFilter) return; //dont run if its not null, means this value is coming from navigation through insights and we need to preserve, not override, we set root only when its null, i.e on factName change. as when we change the fact name, we set breakdown to null.

    const newBreakdown = getDefaultBreakdownForFact(
      filters?.factName || '',
      filters?.viewBy || DEFAULT_VIEW_BY
    );
    setFilters((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        breakdownFilter: newBreakdown,
      };
    });
  }, [breakdowns, hydrated]);

  // ------------------------------------------
  // HELPERS
  // ------------------------------------------
  const updateFilter = useCallback((key: keyof TracesFilters, value: any) => {
    setFilters((prev) => ({
      ...(prev as TracesFilters),
      [key]: value,
    }));
  }, []);

  const updateMostRecent = useCallback(
    (count: number | null) => {
      setFilters((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          mostRecent: count,
          viewBy: count ? 'all' : prev.viewBy, // force safe view in most-recent mode
          // Restore time range when clearing most-recent count
          timeRange:
            count === null
              ? {
                  startTime: convertToUTCDate(range.from)?.toString() || null,
                  endTime: convertToUTCDate(range.to)?.toString() || null,
                }
              : prev.timeRange,
        };
      });
    },
    [range]
  );

  const updateTimeRange = useCallback(
    (newValues: Partial<TimeRange>) => {
      setFilters((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          mostRecent: null, // clear most-recent count when time range is updated
          timeRange: {
            startTime: newValues.startTime
              ? convertToUTCDate(new Date(newValues.startTime))
              : null,
            endTime: newValues.endTime
              ? convertToUTCDate(new Date(newValues.endTime))
              : null,
          },
        };
      });
      if (isOriginFromMeasuresBadge) {
        setIsOriginFromMeasuresBadge(false);
      }
    },
    [isOriginFromMeasuresBadge]
  );

  const resetTimeRange = useCallback(() => {
    setFilters((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        mostRecent: null, // clear most-recent count when time range is reset
        timeRange: {
          startTime: convertToUTCDate(range.from)?.toString() || null,
          endTime: convertToUTCDate(range.to)?.toString() || null,
        },
      };
    });
  }, []);

  const getDefaultBreakdownForFact = (
    fact: string,
    view: ViewByOption
  ): string | null => {
    const factConfig = FACT_VIEW_BREAKDOWN_MAP[fact];
    if (factConfig?.[view]) {
      return factConfig[view]!;
    }

    // 2️⃣ default rule: breakdown = root of all breakdowns, coming from provider.
    if (
      breakdowns &&
      typeof breakdowns === 'object' &&
      !Array.isArray(breakdowns)
    ) {
      const keys = Object.keys(breakdowns);
      return keys.length > 0 ? keys[0] : fact;
    }
    return null;
  };

  const getSupportedViewsForFact = (fact: string): ViewByOption[] => {
    return FACT_VIEW_SUPPORT_MAP[fact] ?? ['all'];
  };

  return {
    workflowLoading,
    transformedWorkflows,
    filters,
    hydrated,
    isOriginFromMeasuresBadge,
    updateFilter,
    updateTimeRange,
    resetTimeRange,
    getSupportedViewsForFact,
    getDefaultBreakdownForFact,
    updateMostRecent,
  };
}

/**
 * Phase 0 — Parse intent from URL (synchronous)
 *  Store them as intent, not as final state
 * Phase 1 — Fetch capabilities (async, fact-dependent)
 *  call APIs to get facts + breakdowns
 * Phase 2 — Resolve final selections (validation layer) - “User wants this” ≠ “App can support this”,
 *  Compare intent vs capabilities, resolve conflicts
 * Phase 3 — Commit state + hydrate UI
 *  Set final filters state, mark as hydrated
 * Phase 4 — URL correction (optional but recommended)
 *  Update URL to reflect resolved state
 */
